/**
 * @fileoverview Participating company management for business events.
 * @module logic/businessEvents/businessEventsParticipation
 */

import { logger, sanitizeError } from "../../utils/logger.js";
import {
  HTTP_CONFLICT,
  getAuthJsonHeaders,
  logDevError,
  resolveExpiredToken,
} from "../../utils/eventApi.js";
import {
  apiMessageOrGeneric,
  getGenericErrorMessage,
} from "../../utils/genericErrorMessages.js";

const FAILED_TO_CREATE_EVENT = "Failed to create event:";
const ERROR_SUBMITTING_EVENT = "Error submitting event:";

const emptyParticipatingCompaniesPage = {
  items: [],
  lastEvaluatedKey: null,
  totalCount: null,
  companyIds: null,
};

/**
 * Reduces a participating company to the summary fields the admin list renders,
 * so requirement and executive arrays never reach list state.
 * @param {Object} company - Participating company from the API
 * @returns {Object} Summary row
 */
const toParticipatingCompanySummary = (company) => ({
  idEvento: company.idEvento,
  idEmpresaCompradora: company.idEmpresaCompradora,
  nombreComercial: company.nombreComercial,
  tiempoBreak: company.tiempoBreak,
  cantidadEjecutivosCompradores:
    company.cantidadEjecutivosCompradores ??
    company.ejecutivosCompradores?.length ??
    0,
  cantidadRequerimientos:
    company.cantidadRequerimientos ?? company.requerimientos?.length ?? 0,
  puedeEliminarse: company.puedeEliminarse ?? true,
});

/**
 * Fetches participating companies for a business event (user-filtered view).
 * @async
 * @param {string} eventId - Business event identifier
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setParticipatingCompanies - State setter for companies
 * @returns {Promise<void>}
 */
export const getParticipatingCompanies = async (
  eventId,
  navigate,
  setParticipatingCompanies,
) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };
  try {
    const userId = localStorage.getItem("userId");
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/${eventId}/participatingCompanies/${userId}`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, async () => {
      await getParticipatingCompanies(
        eventId,
        navigate,
        setParticipatingCompanies,
      );
    });
    if (tokenResult?.redirected) {
      return;
    }
    if (tokenResult) {
      return;
    }

    if (Array.isArray(data)) {
      setParticipatingCompanies(data);
    } else {
      setParticipatingCompanies([]);
    }
  } catch (error) {
    logDevError(error);
    setParticipatingCompanies([]);
  }
};

/**
 * Fetches all participating companies for a business event (admin view).
 * @async
 * @param {string} eventId - Business event identifier
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setParticipatingCompanies - State setter for companies
 * @returns {Promise<void>}
 */
export const getParticipatingCompaniesAdmin = async (
  eventId,
  navigate,
  setParticipatingCompanies,
) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/${eventId}/participatingCompanies`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, async () => {
      await getParticipatingCompaniesAdmin(
        eventId,
        navigate,
        setParticipatingCompanies,
      );
    });
    if (tokenResult?.redirected) {
      return;
    }
    if (tokenResult) {
      return;
    }

    if (Array.isArray(data)) {
      setParticipatingCompanies(data);
    } else {
      setParticipatingCompanies([]);
    }
  } catch (error) {
    logDevError(error);
    setParticipatingCompanies([]);
  }
};

/**
 * Fetches one page of participating companies for the admin event form.
 * @async
 * @param {object} options
 * @param {string} options.eventId
 * @param {Function} options.navigate
 * @param {number} options.limit
 * @param {string|null} [options.lastKey]
 * @param {string} [options.search]
 * @param {boolean} [options.includeAttendance]
 * @returns {Promise<{ items: object[], lastEvaluatedKey: string|null, totalCount: number|null, companyIds: string[]|null }>}
 */
export const getParticipatingCompaniesPage = async ({
  eventId,
  navigate,
  limit,
  lastKey = null,
  search = "",
  includeIds = false,
  includeAttendance = false,
}) => {
  const userId = localStorage.getItem("userId");
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (lastKey) {
    params.set("lastKey", lastKey);
  }
  if (search.trim()) {
    params.set("search", search.trim());
  }
  if (includeIds) {
    params.set("includeIds", "true");
  }
  if (includeAttendance) {
    params.set("includeAttendance", "true");
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/${eventId}/participatingCompanies/${userId}?${params.toString()}`,
      { method: "GET", headers: getAuthJsonHeaders() },
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getParticipatingCompaniesPage({
        eventId,
        navigate,
        limit,
        lastKey,
        search,
        includeIds,
        includeAttendance,
      }),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return emptyParticipatingCompaniesPage;
      }
      return tokenResult.result ?? emptyParticipatingCompaniesPage;
    }
    if (!response.ok) {
      logger.error("Failed to load participating companies page:", {
        data: [response.status, data?.message],
      });
      return emptyParticipatingCompaniesPage;
    }

    return {
      items: Array.isArray(data.items) ? data.items : [],
      lastEvaluatedKey: data.lastEvaluatedKey ?? null,
      totalCount: typeof data.totalCount === "number" ? data.totalCount : null,
      companyIds: Array.isArray(data.companyIds) ? data.companyIds : null,
    };
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return emptyParticipatingCompaniesPage;
  }
};

/**
 * Fetches a single participating company with its requirements and full buyer
 * executives (admin view). Used when opening the requirements editor.
 * @async
 * @param {string} eventId - Business event identifier
 * @param {string} companyId - Buyer company identifier (idEmpresaCompradora)
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|null>} Hydrated participating company, or null on error
 */
export const getParticipatingCompanyAdminDetail = async (
  eventId,
  companyId,
  navigate,
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/${eventId}/participatingCompany/${companyId}/admin`,
      { method: "GET", headers: getAuthJsonHeaders() },
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getParticipatingCompanyAdminDetail(eventId, companyId, navigate),
    );
    if (tokenResult) {
      return tokenResult.redirected ? null : (tokenResult.result ?? null);
    }
    if (!response.ok) {
      logDevError(
        "Failed to load participating company detail:",
        response.status,
        data?.message,
      );
      return null;
    }
    return data;
  } catch (error) {
    logDevError(error);
    return null;
  }
};

/**
 * Fetches buyer companies participating in a business event.
 * @async
 * @param {string} eventId - Business event identifier
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setParticipatingCompanies - State setter for companies
 * @returns {Promise<void>}
 */
export const getBuyerCompanies = async (
  eventId,
  navigate,
  setParticipatingCompanies,
) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/${eventId}/participatingCompanies`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, async () => {
      await getBuyerCompanies(eventId, setParticipatingCompanies);
    });
    if (tokenResult?.redirected) {
      return;
    }
    if (tokenResult) {
      return;
    }

    if (Array.isArray(data)) {
      setParticipatingCompanies(data);
    } else {
      setParticipatingCompanies([]);
    }
  } catch (error) {
    logDevError(error);
    setParticipatingCompanies([]);
  }
};

/**
 * Adds a new participating company to a business event.
 * @async
 * @param {Object} newCompany - Company data to add
 * @param {Function} setParticipatingCompanies - State setter for companies
 * @param {Function} setParticipantError - State setter for error messages
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<void>}
 */
export const addParticipatingCompanyToEvent = async (
  newCompany,
  setParticipatingCompanies,
  setParticipantError,
  navigate,
) => {
  setParticipantError(null);
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify(newCompany),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/participatingCompany`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, async () => {
      await addParticipatingCompanyToEvent(
        newCompany,
        setParticipatingCompanies,
        navigate,
      );
    });
    if (tokenResult?.redirected) {
      return;
    }
    if (tokenResult) {
      return;
    }
    if (!response.ok) {
      setParticipantError("CREATE_ERROR");
      throw new Error(apiMessageOrGeneric(data?.message));
    } else if (setParticipatingCompanies) {
      setParticipatingCompanies((prev) => [
        ...prev,
        toParticipatingCompanySummary(data),
      ]);
    } else {
      // No list setter provided — skip local state update
    }
  } catch (error) {
    logDevError("Error creating participating company:", error);
  }
};

/**
 * Updates an existing participating company's data.
 * @async
 * @param {Object} updatedCompany - Updated company data
 * @param {Function} setParticipatingCompanies - State setter for companies
 * @param {Function} setParticipantError - State setter for error messages
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<void>}
 */
export const updateParticipatingCompany = async (
  updatedCompany,
  setParticipatingCompanies,
  setParticipantError,
  navigate,
) => {
  setParticipantError(null);
  const options = {
    method: "PUT",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify(updatedCompany),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/participatingCompany`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, async () => {
      await updateParticipatingCompany(
        updatedCompany,
        setParticipatingCompanies,
        navigate,
      );
    });
    if (tokenResult?.redirected) {
      return;
    }
    if (tokenResult) {
      return;
    }

    if (!response.ok) {
      if (response.status === HTTP_CONFLICT) {
        setParticipantError("USER_IN_APPOINTMENT");
        throw new Error(apiMessageOrGeneric(data?.message));
      } else {
        setParticipantError("UPDATE_ERROR");
        throw new Error(apiMessageOrGeneric(data?.message));
      }
    } else if (setParticipatingCompanies) {
      setParticipatingCompanies((prev) =>
        prev.map((pc) =>
          pc.idEmpresaCompradora === data.idEmpresaCompradora
            ? toParticipatingCompanySummary(data)
            : pc,
        ),
      );
    } else {
      // No list setter provided — skip local state update
    }
  } catch (error) {
    logDevError("Error updating participating company:", error);
  }
};

const buildRequirementRequestBody = (newRequirement) => {
  const payload = { ...newRequirement };
  if (!newRequirement.nuevaImagenCargada) {
    delete payload.ejemploTecnico;
    return payload;
  }
  if (newRequirement.ejemploTecnico) {
    payload.ejemploTecnico = {
      nombreImagen: newRequirement.ejemploTecnico.name,
    };
  }
  return payload;
};

const uploadRequirementTechnicalExample = async (
  presignedUrl,
  newRequirement,
) => {
  const uploadResponse = await fetch(presignedUrl, {
    method: "PUT",
    body: newRequirement.ejemploTecnico,
    headers: {
      "Content-Type":
        newRequirement.ejemploTecnico.type || "application/octet-stream",
    },
  });
  if (!uploadResponse.ok) {
    throw new Error(
      getGenericErrorMessage("FAILED_TO_UPLOAD_FILE", {
        statusText: uploadResponse.statusText,
      }),
    );
  }
};

/**
 * Adds a requirement to a participating company with optional image upload.
 * @async
 * @param {Object} participant - Participating company object
 * @param {Object} newRequirement - Requirement data including optional ejemploTecnico file
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|undefined>} Created requirement or undefined on error
 */
export const addRequirementToParticipant = async (
  participant,
  newRequirement,
  navigate,
) => {
  try {
    if (!newRequirement.nuevaImagenCargada) {
      delete newRequirement.ejemploTecnico;
    }

    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/${participant.idEvento}/participatingCompany/${participant.idEmpresaCompradora}/requirement`,
      {
        method: "PUT",
        headers: getAuthJsonHeaders(),
        body: JSON.stringify(buildRequirementRequestBody(newRequirement)),
      },
    );

    if (response.ok) {
      const data = await response.json();

      if (data.presignedUrl && newRequirement.nuevaImagenCargada) {
        await uploadRequirementTechnicalExample(
          data.presignedUrl,
          newRequirement,
        );
      }
      return data.requirement;
    }

    const errorData = await response.json();
    const tokenResult = await resolveExpiredToken(
      errorData,
      navigate,
      async () => {
        await addRequirementToParticipant(
          participant,
          newRequirement,
          navigate,
        );
      },
    );
    if (tokenResult?.redirected) {
      return undefined;
    }
    if (tokenResult) {
      return undefined;
    }
    logDevError(FAILED_TO_CREATE_EVENT, response.status, errorData.message);
    return undefined;
  } catch (error) {
    logDevError(ERROR_SUBMITTING_EVENT, error);
    return undefined;
  }
};

/**
 * Deletes a requirement from a participating company.
 * @async
 * @param {Object} participant - Participating company object
 * @param {Object} newRequirement - Requirement to delete with _id property
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<void>}
 */
export const deleteRequirementFromParticipant = async (
  participant,
  newRequirement,
  navigate,
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/${participant.idEvento}/participatingCompany/${participant.idEmpresaCompradora}/requirement/${newRequirement._id}`,
      {
        method: "DELETE",
        headers: getAuthJsonHeaders(),
        body: JSON.stringify(newRequirement),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      const tokenResult = await resolveExpiredToken(
        errorData,
        navigate,
        async () => {
          await deleteRequirementFromParticipant(
            participant,
            newRequirement,
            navigate,
          );
        },
      );
      if (tokenResult?.redirected) {
        return undefined;
      }
      if (tokenResult) {
        return undefined;
      }
      logDevError(FAILED_TO_CREATE_EVENT, response.status, errorData.message);
    }
    return undefined;
  } catch (error) {
    logDevError(ERROR_SUBMITTING_EVENT, error);
    return undefined;
  }
};

/**
 * Deletes a participating company from a business event.
 * @async
 * @param {Object} participant - Participating company with idEvento and idEmpresaCompradora
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<boolean>} True if deletion was successful
 */
export const deleteParticipatingCompany = async (participant, navigate) => {
  const options = {
    method: "DELETE",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/${participant.idEvento}/participatingCompany/${participant.idEmpresaCompradora}`,
      options,
    );
    const data = await response.json();

    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      deleteParticipatingCompany(participant, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return false;
      }
      return tokenResult.result;
    }

    return response.ok;
  } catch (error) {
    logDevError("Error deleting banner:", error);
    return false;
  }
};
